import { useState } from "react";
import { createContext } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  changeDiscountCodeActiveAdminService,
  createDiscountCodeAdminService,
  deleteDiscountCodeAdminService,
  editDiscountCodeAdminService,
  getCategoriesAdminService,
  getDiscountCodesAdminService,
  getProductsAdminService,
  getUsersAdminService,
} from "./../../../services/adminServices";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import { convertProductTypeToText } from "../../../utils/enum";

export const discountCodeAdminContext = createContext({
  discountCodes: [],
  setDiscountCodes: () => {},
  discountCode: {},
  setDiscountCode: () => {},
  filter: "",
  setFilter: () => {},
  sort: "",
  setSort: () => {},
  start: "",
  setStart: () => {},
  end: "",
  setEnd: () => {},
  isActive: "",
  setIsActive: () => {},
  type: "",
  setType: () => {},
  pageCount: null,
  setPageCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  itemsCount: null,
  setItemsCount: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  handleShowModal: () => {},
  handleHideModal: () => {},
  handleGetCategories: () => {},
  handleGetProducts: () => {},
  handleGetUsers: () => {},
  handleGetDiscountCodes: () => {},
  handleCreateDiscountCode: () => {},
  handleEditDiscountCode: () => {},
  handleChangeDiscountCodeActive: () => {},
  handleDeleteDiscountCode: () => {},
  generateRandomDiscountCode: () => {},
});

const DiscountCodeAdminContext = ({ children }) => {
  const dispatch = useDispatch();

  const { product, category, user } = useParams();

  const [discountCodes, setDiscountCodes] = useState([]);
  const [discountCode, setDiscountCode] = useState({});
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("");
  const [type, setType] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [isActive, setIsActive] = useState("");
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsCount, setItemsCount] = useState(100);
  const [isShowModal, setIsShowModal] = useState(false);

  // Modal Operation
  const handleShowModal = (val, setValue) => {
    setIsShowModal(true);

    if (val) {
      setDiscountCode(val);
      setValue("title", val.title);
      setValue("code", val.code);
      setValue("type", val.type);
      setValue("percentage", val.percentage);
      setValue("amount", val.amount);
      setValue("minPurchase", val.minPurchase);
      setValue("maxDiscount", val.maxDiscount);
      setValue("products", val.products);
      setValue("categories", val.categories);
      setValue("singleUse", val.singleUse);
      setValue("limit", val.limit);
      setValue(
        "products",
        val.products?.map((item) => ({
          value: item._id,
          label: `${item.title} - ${item.code} - ${convertProductTypeToText(
            item.type
          )}`,
        }))
      );
      setValue(
        "categories",
        val.categories?.map((item) => ({
          value: item._id,
          label: item.title,
        }))
      );
      setValue(
        "users",
        val.users?.map((item) => ({
          value: item._id,
          label: `${item.firstName} ${item.lastName} - ${item.phoneNumber}`,
        }))
      );
      setValue("expiredAt", val.expiredAt);
      setValue("description", val.description);
    }
  };
  const handleHideModal = () => {
    setIsShowModal(false);
    setDiscountCode({});
  };

  // Get Categories
  const handleGetCategories = async (filter, callback) => {
    const objData = {
      filter,
      categoryId: "ALL",
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getCategoriesAdminService(objData);
      if (status === 200) {
        !!callback &&
          callback(
            data.result?.categories?.map((item) => ({
              value: item._id,
              label: item.title,
            }))
          );
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Products
  const handleGetProducts = async (filter, callback) => {
    const objData = {
      filter,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getProductsAdminService(objData);
      if (status === 200) {
        !!callback &&
          callback(
            data.result?.products?.map((item) => ({
              value: item._id,
              label: `${item.title} - ${item.code} - ${convertProductTypeToText(
                item.type
              )}`,
            }))
          );
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Users
  const handleGetUsers = async (filter, callback) => {
    const objData = {
      filter,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getUsersAdminService(objData);
      if (status === 200) {
        !!callback &&
          callback(
            data.result?.users?.map((item) => ({
              value: item._id,
              label: `${item.firstName} ${item.lastName} - ${item.phoneNumber}`,
            }))
          );
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Discount Codes
  const handleGetDiscountCodes = async (event) => {
    event?.preventDefault();

    const objData = {
      ...(start && { start }),
      ...(end && { end }),
      ...(type && { type }),
      ...(product && { product }),
      ...(category && { category }),
      ...(user && { user }),
      ...(isActive && { isActive }),
      filter,
      pageNumber,
      sort,
      itemsCount,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getDiscountCodesAdminService(objData);
      if (status === 200) {
        setDiscountCodes(data.result?.discountCodes);
        setPageCount(data?.result?.allPagesCount);
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Discount Code
  const handleCreateDiscountCode = async (discountCodeData) => {
    const objData = {
      ...discountCodeData,
      products: discountCodeData?.products?.map((item) => item.value),
      categories: discountCodeData?.categories?.map((item) => item.value),
      users: discountCodeData?.users?.map((item) => item.value),
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createDiscountCodeAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetDiscountCodes();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Edit Discount Code
  const handleEditDiscountCode = async (discountCodeData) => {
    const objData = {
      id: discountCode._id,
      ...discountCodeData,
      products: discountCodeData?.products?.map((item) => item.value),
      categories: discountCodeData?.categories?.map((item) => item.value),
      users: discountCodeData?.users?.map((item) => item.value),
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await editDiscountCodeAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetDiscountCodes();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Change Discount Code Active
  const handleChangeDiscountCodeActive = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await changeDiscountCodeActiveAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetDiscountCodes();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Delete Discount Code
  const handleDeleteDiscountCode = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await deleteDiscountCodeAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetDiscountCodes();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Generate Random Discount Code
  const generateRandomDiscountCode = (length = 10) => {
    const letters = "0123456789ABCDEF";
    let color = "NB-";
    for (let i = 0; i < length; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  return (
    <discountCodeAdminContext.Provider
      value={{
        discountCodes,
        setDiscountCodes,
        discountCode,
        setDiscountCode,
        filter,
        setFilter,
        sort,
        setSort,
        type,
        setType,
        start,
        setStart,
        end,
        setEnd,
        isActive,
        setIsActive,
        pageCount,
        setPageCount,
        pageNumber,
        setPageNumber,
        itemsCount,
        setItemsCount,
        isShowModal,
        setIsShowModal,
        handleShowModal,
        handleHideModal,
        handleGetCategories,
        handleGetProducts,
        handleGetUsers,
        handleGetDiscountCodes,
        handleCreateDiscountCode,
        handleEditDiscountCode,
        handleChangeDiscountCodeActive,
        handleDeleteDiscountCode,
        generateRandomDiscountCode,
      }}
    >
      {children}
    </discountCodeAdminContext.Provider>
  );
};

export default DiscountCodeAdminContext;
